import { Application } from '../application/application';
import { XHRDataSource } from '../data-source/xhr-source';
import { DatumReader, Datum, DataProvider, MetaData, AnimationMode, SpatialCoordinate, TemporalCoordinate } from '../data-source/types';
import * as util from '../data-source/util';
import { updateMeta, createBlankAnimationDistribution, applyDataToAnimationDistribution, getAnimationDistribution, AnimationDistribution } from '../data-source/data-list';
import { VisualizationUpdateLevel } from '../data-source/visualizer';

import { DataList } from '../data-source/data-list';
import { ScalarFieldManager } from '../worker/scalar-field-manager';
import { ImpactMap, GoiImpact, BandImpact } from '../scalar-field/scalar-field';
import { ModelRunProfile, ModelProject, ModelProjectInterval, ModelRun, ModelingTypeDefinition } from '../modeling/modeling-common';
import { CursorMillis } from '../timeline/timeline-common';
import { Geometry, geometry2SpatialCoordinate, ImpactParameters } from './modeling-common';
import { ModelingSourceMapVisualizer } from './mdl-ms-visualizer';
import { PoiDataSource } from '../poi/poi';
import { GoiDatum } from '../poi/poi-common';
import { ImpactMonitorMapVisualizer, ImpactMonitorVisualizerControl } from './mdl-mm-visualizer';

import { Subscription } from 'rxjs';
import { XHRResult } from '../xhr/xhr';
import { CombineMode } from '../worker/scalar-field-manager';
import { WindFieldMapVisualizer } from './mdl-wf-visualizer';

let modelingDataSource: ModelingDataSource = undefined;
let impactMonitorDataSource: ImpactMonitorDataSource = undefined;

export const initializer: { [key: string]: (application: Application) => void } = {
	"modeling": (application: Application) => {
		console.log('Initializing modeling...');
		impactMonitorDataSource = new ImpactMonitorDataSource(application);
		modelingDataSource = new ModelingDataSource(application);
	}
}

export const authenticator: { [key: string]: () => void } = {
	"modeling": () => {
		console.log('Authenticating modeling...');
		getDataSource().authenticate();
	}
}

export const getDataSource = (): ModelingDataSource => modelingDataSource;
export const getImpactMonitorDataSource = (): ImpactMonitorDataSource => impactMonitorDataSource;

export const setImpactScore = (impacts: ImpactMap, goiMap: { [key: string]: ImpactMonitorDatum }, impactParameters: ImpactParameters): void => {
	
	let highestMonitor: GoiImpact = undefined;
	Object.entries(impacts.gois)
		.forEach(goiImpactEntry => {
			let goiId = goiImpactEntry[0];
			let goiImpact = goiImpactEntry[1]
			let goi = goiMap[goiId];

			let highestBand: BandImpact = undefined;

			Object.entries(goiImpact.bands)
				.forEach(bandImpactEntry => {
					let bandValue = bandImpactEntry[0];
					let bandImpact = bandImpactEntry[1];

					let sizeScore = goi.size > 0.0 ? bandImpact.size / goi.thresholdSize : bandImpact.size > 0.0 ? goi.thresholdSize : 0.0;
					let scalarScore = sizeScore > 0.0 ? (Math.max(Math.min(bandImpact.value, impactParameters.maxValue), impactParameters.minValue) - impactParameters.minValue) / (impactParameters.maxValue - impactParameters.minValue) : 0.0;
					let score = (sizeScore * impactParameters.sizeWeight + scalarScore * impactParameters.scalarWeight) / (impactParameters.sizeWeight + impactParameters.scalarWeight);
					bandImpact.score = { scalar: scalarScore, size: sizeScore, combined: score };
					highestBand = !highestBand || highestBand.score.combined < bandImpact.score.combined ? bandImpact : highestBand;
				});
			goiImpact.highestBand = highestBand !== undefined ? highestBand.value : undefined;
			if (highestBand) {
				highestMonitor = !highestMonitor || highestMonitor.bands[highestMonitor.highestBand].score.combined < highestBand.score.combined ? goiImpact : highestMonitor;
			}
		})
	impacts.highestGoi = highestMonitor !== undefined ? highestMonitor.goiId : undefined;
}


export class ModelingDataSource extends XHRDataSource {
	private scalarFieldManagers: Record<string, ScalarFieldManager>;
	private modelingSourceMapVisualizer: ModelingSourceMapVisualizer;
	private impactMonitorSubscriptions: Record<string, Subscription>;

	public constructor(application: Application) {
		super({
			name: 'modeling-project',
			url: `/${encodeURI(application.getDomain())}/modeling/selection/project`
		}, application);

		this.scalarFieldManagers = {};
		this.impactMonitorSubscriptions = {};

		this.modelingSourceMapVisualizer = <ModelingSourceMapVisualizer>this.addVisualizer(new ModelingSourceMapVisualizer('source', this, 'tlmd-source icon-target'));
		

	}

	protected typeAdded(typeId: string): void {
		//		super.typeAdded(typeId);
		//the call to super is delayed until after authentication
		this.getScalarFieldManager(typeId);//initiates sign in
	}

	public removeType(typeId: string): void {
		super.removeType(typeId);
		this.modelingSourceMapVisualizer.refresh(VisualizationUpdateLevel.data);
	}

	public authenticate(signInUrl?: string, credentials?: string): void {

		// gives web workers a change to access secured resources with XHR
		signInUrl = signInUrl ? signInUrl : `/${encodeURI(this.getApplication().getDomain())}`;
		credentials = credentials ? credentials : this.getApplication().getDigester().getCredentials();

		Object.keys(this.scalarFieldManagers).forEach(type => {
			this.scalarFieldManagers[type].authenticate(signInUrl, credentials);
		});
	}

	private authenticateScalarFieldManager(scalarFieldManager: ScalarFieldManager, signInUrl?: string, credentials?: string): void {
		// gives web workers a change to access secured resources with XHR
		signInUrl = signInUrl ? signInUrl : `/${encodeURI(this.getApplication().getDomain())}`;
		credentials = credentials ? credentials : this.getApplication().getDigester().getCredentials();
		scalarFieldManager.authenticate(signInUrl, credentials);
	}

	protected createDataList(type: string, versionProvider: DatumReader, data?: Datum[]): DataList {
		return new ModelingDataList(this.getScalarFieldManager(type), type, versionProvider, data);
	}

	public getScalarFieldManager(type: string): ScalarFieldManager {
		if (type) {
			if (!this.scalarFieldManagers[type]) {
				const modellingGrid = (<ModelingTypeDefinition>this.getDefinitions()[type]).modellingGrid;
				this.scalarFieldManagers[type] = new ScalarFieldManager(this.getApplication(), modellingGrid , (authenticated: boolean) => {

					this.impactMonitorSubscriptions[type] = getImpactMonitorDataSource().getData()['any'].getMetaObservable().subscribe(meta => {
						//this does not get called  while it should??? No time to investigate and the call  5 rows down solves the impact monitoring bug
						this.scalarFieldManagers[type].setImpactMonitors(<ImpactMonitorDatum[]>getImpactMonitorDataSource().getData()['any'].getData() );
					}, (error) => { console.log(`Error  while subscribing`, error) });
					super.typeAdded(type);
				});

				
				
				this.scalarFieldManagers[type].setImpactMonitors(<ImpactMonitorDatum[]>getImpactMonitorDataSource().getData()['any'].getData());

				this.authenticateScalarFieldManager(this.scalarFieldManagers[type]);
			}

			return this.scalarFieldManagers[type];
		} else {
			return undefined;
		}
	}

	public getWindFieldId(type: string): { hpc: string, model: string } | undefined {
		if (type && this.getDefinitions()[type]) {
			const modelingTypeDefinition = <ModelingTypeDefinition>this.getDefinitions()[type];
			const windFieldId = (modelingTypeDefinition.grid.supportsWindFields && modelingTypeDefinition.output?.wind2d === true)
				? { hpc: modelingTypeDefinition.hpc, model: modelingTypeDefinition.model }
				: undefined;
			return windFieldId
		}
		return undefined;
	}



	public getData(types?: string[]): { [key: string]: ModelingDataList } {
		return <{ [key: string]: ModelingDataList }>super.getData(types);
	}

	protected getDataList(type: string): ModelingDataList {
		return <ModelingDataList>super.getDataList(type);
	}

	public getModelingSourceData(types?: string[]): { [key: string]: ModelingSourceDataProvider } {
		return (types ? types : this.getTypes())
			.filter(type => this.getTypes().indexOf(type) !== -1)
			.reduce((dataMap, type) => {
				dataMap[type] = this.getDataList(type).getModelingSourceDataProvider();
				return dataMap;
			}, <{ [key: string]: ModelingSourceDataProvider }>{});
	}

	// Modeling's implementation of DatumReader which should be quicker than the the default implementation 
	public isTemporal = (_datum: any) => true
	public isMoment = (_datum: any) => false;
	public isSpatial = (_datum: any) => false;
	public isPoint = (_datum: any) => false;
	public isArea = (_datum: any) => false;
	public isVersional = (_datum: any) => true;


}

export class ModelingDataList extends DataList {
	private profile: ModelRunProfile[] = [];
	private categories: Map<string, ModelProject[]>
	private modelingSources: ModelingSourceDataProvider;

	public constructor(private scalarFieldManager: ScalarFieldManager, type: string, datumReader: DatumReader, data?: Datum[]) {
		super(type, datumReader, data);
		this.modelingSources = new ModelingSourceDataProvider(this);
		this.categories = new Map();
	}

	public getProfile(): ModelRunProfile[] {
		return this.profile;
	}

	protected shouldBeReplaced(oldDatum: ModelProject, newDatum: ModelProject): boolean {
		let superWantsToReplace = super.shouldBeReplaced(oldDatum, newDatum);
		if (!superWantsToReplace) {
			//check if profile is the same
			if (oldDatum.profile.length === newDatum.profile.length &&
				(oldDatum.profile.length == 0 || (
					oldDatum.profile[oldDatum.profile.length - 1].end === newDatum.profile[newDatum.profile.length - 1].end &&
					oldDatum.profile[0].start === newDatum.profile[0].start))) {
				//same profile so ne replacement needed
				return false;
			}
			return true;
		} else {
			return true;
		}
	}

	public applyFilter(updateTimestamp?: number): number[] {
		let filteredIndices = super.applyFilter(updateTimestamp);

		//build profile based on all projects
		let data = <ModelProject[]>this.getFilteredData();
		this.profile.splice(0, this.profile.length);
		if (data && data.length > 0) {
			data.forEach((project, index) => {
				let projectProfile = this.mapToProfile(project.id, project.profile);
				if (index === 0) {
					this.profile.splice(0, 0, ...projectProfile);
				} else {
					let i = 0;
					projectProfile.forEach(projectProfileInterval => {
						i = this.addToProfile(this.profile, projectProfileInterval, i);
					});
				}
			});
		}

		this.categorizeModelProjects((mp: ModelProject) => mp.parameters.tag, data);
		//extract modeling sources
		this.modelingSources.handleData();

		//console.log('poi source meta= %o', getImpactMonitorDataSource().getMeta());


		this.scalarFieldManager.handleData(this.getType(), data, this.getProfile(), this.getCategorizedModelProjects(), this.getAnimationKeys(), this.getAnimationInterval());


		return filteredIndices;
	}

	public getModelingSourceDataProvider(): ModelingSourceDataProvider {
		return this.modelingSources;
	}

	private mapToProfile(modelProjectId: string, intervals: ModelProjectInterval[]): ModelRunProfile[] {
		if (intervals && intervals.length > 0) {
			return intervals.map(interval => {
				let projectProfileInterval = {
					start: interval.start,
					end: interval.end,
					properties: { runs: <{ [key: string]: ModelRun }>{} }
				};
				projectProfileInterval.properties.runs[modelProjectId] = {
					runId: interval.runId,
					version: interval.version,
					status: interval.status,
					range: interval.range,
					geometry: interval.geometry
				};
				return projectProfileInterval;
			});
		}
		return [];
	}

	private addToProfile(profile: ModelRunProfile[], part: ModelRunProfile, from: number): number {
		//search in profile (starting from the given index) for position to insert the given profile part
		//i.e. start time must be inside or later then given profile part

		let i = from;
		//skip all parts in profile that end before the given part starts
		while (i < profile.length && profile[i].end <= part.start) i++;

		if (i < profile.length) {

			if (profile[i].start >= part.end) {//given part ends before any succeeding profile part starts:no overlap -> insert part before i (i.e. at i) and return index of profile part that succeeds inserted part ( i.e. i+1 )
				profile.splice(i, 0, part);//add(i,part);
				return i + 1;
			} else {
				let si = i;//remember start
				//find index of profile part that starts later then given part
				while (i < profile.length && profile[i].start <= part.end) i++;
				let ei = i;//remember end
				//profile parts from si to ei (exclusive) overlap with given part
				let newSubProfile = this.createProfile(profile.splice(si, 1)[0], part);
				ei--;
				while (si < ei) {
					part = newSubProfile.splice(newSubProfile.length - 1, 1)[0];
					newSubProfile.splice(newSubProfile.length, 0, ...this.createProfile(profile.splice(si, 1)[0], part));
					ei--;
				}


				profile.splice(si, 0, ...newSubProfile);
				return si + newSubProfile.length - 1;
			}
		} else {// no overlap, complete profile precedes part -> append to the end of the list and return the index where to search from (i.e. the size of the list)
			profile.push(part);
			return profile.length;
		}
	}

	private createProfile(part0: ModelRunProfile, part1: ModelRunProfile): ModelRunProfile[] {
		let profile: ModelRunProfile[] = [];
		let first = part0.start <= part1.start ? part0 : part1;
		let other = part0.start <= part1.start ? part1 : part0;

		if (other.start == first.start) { //other starts when the first starts
			if (other.end < first.end) { //other ends before first ends -> profile has length 2
				profile.push(<ModelRunProfile>{ start: first.start, end: other.end, properties: { runs: Object.assign({}, first.properties.runs, other.properties.runs) } });
				profile.push(<ModelRunProfile>{ start: other.end, end: first.end, properties: { runs: Object.assign({}, first.properties.runs) } });
			} else if (other.end == first.end) { // other ends when first ends (same interval) -> profile has length 1
				profile.push(<ModelRunProfile>{ start: first.start, end: first.end, properties: { runs: Object.assign({}, first.properties.runs, other.properties.runs) } });
			} else { //other ends after first ends -> profile has length 2
				profile.push(<ModelRunProfile>{ start: first.start, end: first.end, properties: { runs: Object.assign({}, first.properties.runs, other.properties.runs) } });
				profile.push(<ModelRunProfile>{ start: first.end, end: other.end, properties: { runs: Object.assign({}, other.properties.runs) } });
			}
		} else if (other.start < first.end) { //other starts before first ends
			if (other.end < first.end) { //other ends before first ends -> profile has length 3
				profile.push(<ModelRunProfile>{ start: first.start, end: other.start, properties: { runs: Object.assign({}, first.properties.runs) } });
				profile.push(<ModelRunProfile>{ start: other.start, end: other.end, properties: { runs: Object.assign({}, first.properties.runs, other.properties.runs) } });
				profile.push(<ModelRunProfile>{ start: other.end, end: first.end, properties: { runs: Object.assign({}, first.properties.runs) } });
			} else if (other.end == first.end) { // other ends when first ends (same interval) -> profile has length 2
				profile.push(<ModelRunProfile>{ start: first.start, end: other.start, properties: { runs: Object.assign({}, first.properties.runs) } });
				profile.push(<ModelRunProfile>{ start: other.start, end: other.end, properties: { runs: Object.assign({}, first.properties.runs, other.properties.runs) } });
			} else { //other ends after first ends -> profile has length 3
				profile.push(<ModelRunProfile>{ start: first.start, end: other.start, properties: { runs: Object.assign({}, first.properties.runs) } });
				profile.push(<ModelRunProfile>{ start: other.start, end: first.end, properties: { runs: Object.assign({}, first.properties.runs, other.properties.runs) } });
				profile.push(<ModelRunProfile>{ start: first.end, end: other.end, properties: { runs: Object.assign({}, other.properties.runs) } });
			}
		} else { //other start when first has already ended -> profile has length 2 
			profile.push(<ModelRunProfile>{ start: first.start, end: first.end, properties: { runs: Object.assign({}, first.properties.runs) } });
			profile.push(<ModelRunProfile>{ start: other.start, end: other.end, properties: { runs: Object.assign({}, other.properties.runs) } });
		}

		return profile;
	}

	public getCategorizedModelProjects(): Map<string, ModelProject[]> {
		return this.categories;
	}

	/**
	 * Put the data items in group arrays as determined by the this.groupSpecifier. This
	 * method then returns a map from group name to list of data items within that group.
	 * @param dataItems the data items to group
	 * @return the map of group lists.
	 */
	private categorizeModelProjects(categorizer: (modelProject: ModelProject) => string, modelprojects: ModelProject[]): Map<string, ModelProject[]> {
		this.categories = new Map();
		modelprojects.forEach(modelProject => {
			const category = categorizer(modelProject);
			let categoryList = this.categories.get(category);
			if (!categoryList) {
				categoryList = [];
				this.categories.set(category, categoryList);
			}
			categoryList.push(modelProject);
		});
		return this.categories;
	}

	public getOverlayElementIds(combineMode: CombineMode): string[] {
		if (combineMode === CombineMode.all) {
			return ['all'];
		} else if (combineMode === CombineMode.category) {
			return Array.from(this.getCategorizedModelProjects().keys());
		} else {
			return this.getDataIds();
		}
	}

}

export interface ModelingSourceDatum extends Geometry, Datum {
	projects: { id: string, type: string }[]
}

export const modelingSourceDataReader: DatumReader = {
	getVersion: (_datum: any) => <number>undefined,
	getTime: (_datum: any) => <number>undefined,
	getStart: (datum: any) => <number>datum.start,
	getEnd: (datum: any) => <number>datum.end,
	getLat: (datum: any) => <number>datum.lat,
	getLng: (datum: any) => <number>datum.lng,
	getNorth: (datum: any) => <number>datum.north,
	getEast: (datum: any) => <number>datum.east,
	getSouth: (datum: any) => <number>datum.south,
	getWest: (datum: any) => <number>datum.west,
	compareByTime: (datum0: TemporalCoordinate, datum1: TemporalCoordinate) => util.compareByTime(datum0, datum1),
	temporalHash: (datum: Datum) => util.datumTemporalHash(datum),
	isTemporal: (_datum: any) => true,
	isMoment: (_datum: any) => false,
	isRange: (_datum: any) => true,
	isPerpetual: (_datum: any) => false,
	isSince: (_datum: any) => false,
	isUntil: (_datum: any) => false,
	hasStart: (_datum: any) => true,
	hasEnd: (_datum: any) => true,
	getDuration: (datum: any) => datum.end - datum.start,
	compareByLocation: (datum0: SpatialCoordinate, datum1: SpatialCoordinate) => util.compareByLocation(datum0, datum1),
	spatialHash: (datum: Datum) => util.datumSpatialHash(datum),
	isSpatial: (_datum: any) => true,
	isPoint: (datum: any) => util.isPoint(datum),
	isArea: (datum: any) => util.isArea(datum),
	isVersional: (_datum: any) => false
}

export class ModelingSourceDataProvider implements DataProvider {
	private meta: MetaData;
	private data: ModelingSourceDatum[];
	private animationDistribution: { [key: number]: AnimationDistribution };

	public constructor(private dataList: ModelingDataList) {
		this.data = [];
		this.meta = {
			start: undefined,
			end: undefined,
			north: undefined,
			east: undefined,
			south: undefined,
			west: undefined,
			version: undefined,
			lastUpdate: Date.now(),
			selected: []
		};

		this.animationDistribution = applyDataToAnimationDistribution(createBlankAnimationDistribution(this.dataList.getAnimationDistribution()), this.data, modelingSourceDataReader);
	}

	public handleData() {
		let profile = this.dataList.getProfile();
		this.data = profile.flatMap(interval =>
			Object.values(
				Object.keys(interval.properties.runs)
					.filter(projectId => interval.properties.runs[projectId].geometry)
					.map(projectId => Object.assign({ projects: [{ id: projectId, type: this.dataList.getType() }] }, interval.properties.runs[projectId].geometry))
					.reduce((sourceMap, source) => {
						if (sourceMap[source.id]) {
							sourceMap[source.id].projects.push(...source.projects);
						} else {
							sourceMap[source.id] = Object.assign({ start: interval.start, end: interval.end, active: true, deleted: false }, <ModelingSourceDatum>geometry2SpatialCoordinate(source));
						}
						return sourceMap;
					}, <{ [key: string]: ModelingSourceDatum }>{})
			)
		);
		//console.log('modelingSources: %o', this.data);

		updateMeta(this.meta, this.data, modelingSourceDataReader);

		this.animationDistribution = applyDataToAnimationDistribution(createBlankAnimationDistribution(this.dataList.getAnimationDistribution()), this.data, modelingSourceDataReader);
	}

	public getMeta(): MetaData {
		return this.meta;
	}

	public getData(animationPosition?: CursorMillis, animationMode?: AnimationMode): Datum[] {

		let animationIndexFilter: (datum: Datum, index: number) => boolean = undefined;
		let animationDatumFilter: (datum: Datum) => boolean = undefined;
		if (animationPosition !== undefined && animationMode !== undefined && animationMode !== AnimationMode.always) {
			if (this.animationDistribution[animationPosition.center] !== undefined) {
				animationIndexFilter = (_datum: Datum, index: number) => (<number[]>(<any>this.animationDistribution[animationPosition.center])[AnimationMode[animationMode]]).includes(index);
			} else {
				animationDatumFilter = (datum: Datum) => <boolean>(<any>getAnimationDistribution(animationPosition, datum, modelingSourceDataReader))[AnimationMode[animationMode]];
			}
		}
		return (animationIndexFilter
			? this.data.filter(animationIndexFilter)
			: animationDatumFilter
				? this.data.filter(animationDatumFilter)
				: this.data);
	}
}

export interface ImpactMonitorDatum extends GoiDatum {
	properties: {
		impactMonitor: {
			sensitivity: number
		}
	},
	thresholdSize: number;

}

export class ImpactMonitorDataSource extends PoiDataSource {
	private impactMonitorMapVisualizer: ImpactMonitorMapVisualizer;
	private impactMonitorVisualizerControl: ImpactMonitorVisualizerControl;

	public constructor(application: Application) {
		super(application, 'impactMonitor');
		this.impactMonitorMapVisualizer = new ImpactMonitorMapVisualizer('monitors', this, 'tlmd-monitor');
		this.impactMonitorVisualizerControl = new ImpactMonitorVisualizerControl(application, this.impactMonitorMapVisualizer);
		this.setControls(this.impactMonitorVisualizerControl);
		this.addVisualizer(this.impactMonitorMapVisualizer);
	}

	public addControlContainer(containerId: string) {
		this.impactMonitorVisualizerControl.addContainerId(containerId);
	}
	public removeControlContainer(containerId: string) {
		this.impactMonitorVisualizerControl.removeContainerId(containerId);
	}


	protected processResponse(response: XHRResult): any {
		let data = <ImpactMonitorDatum[]>super.processResponse(response);

		if (data && data.length > 0) {

			for (let i = 0; i < data.length; i++) {
				if (!data[i].properties) {
					data[i].properties = { impactMonitor: { sensitivity: 1.0 } };
				} else if (!data[i].properties.impactMonitor) {
					data[i].properties.impactMonitor = { sensitivity: 1.0 };
				} else if (data[i].properties.impactMonitor.sensitivity == undefined) {
					data[i].properties.impactMonitor.sensitivity = 1.0;
				}

				data[i].thresholdSize = data[i].size > 0.0 ? data[i].size / data[i].properties.impactMonitor.sensitivity : data[i].properties.impactMonitor.sensitivity;

			}
		}

		return data;

	}

}
